import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ShieldCheck,
  Lock,
  CreditCard,
  Truck,
  ChevronRight,
  Check,
  Package,
  LogIn,
  UserPlus,
  Eye,
  EyeOff,
  Store,
} from 'lucide-react';
import { useCartStore, useAppStore } from '@/lib/store';
import { useAdminStore } from '@/lib/admin-store';
import { useUserStore } from '@/lib/user-store';
import { useTranslation, useCurrency } from '@/hooks/useTranslation';
import toast from 'react-hot-toast';

import type { DeliveryMethod, Address } from '@/types';
import { SearchableSelect, type SearchableOption } from '@/components/SearchableSelect';
import { FormLabel, FieldError, RequiredNote } from '@/components/FormLabel';
import { focusFirstError, inputBorder, isBlank, isEmail, type FieldErrors } from '@/lib/form-validation';
import { US_STATES, isUnitedStates } from '@/lib/us-states';
import { checkPassword } from '@/lib/password-policy';

type Step = 'shipping' | 'payment' | 'confirm';
type AuthTab = 'login' | 'register';

/* ========== AUTH GATE COMPONENT ========== */
const AuthGate: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const login = useUserStore((s) => s.login);
  const register = useUserStore((s) => s.register);

  const [authTab, setAuthTab] = useState<AuthTab>('login');
  const [showPw, setShowPw] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [regForm, setRegForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '',
  });

  const handleLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const ok = await login(loginForm.email, loginForm.password);
    if (ok) { toast.success(t.auth.login + '!'); onSuccess(); }
    else { toast.error(t.auth.invalidCredentials); }
  };

  const handleRegister = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regForm.email)) { toast.error(t.auth.invalidEmail); return; }
    if (!checkPassword(regForm.password).ok) { toast.error(t.auth.passwordMinLength); return; }
    if (regForm.password !== regForm.confirmPassword) { toast.error(t.auth.passwordsDontMatch); return; }
    const result = await register({
      firstName: regForm.firstName.trim(),
      lastName: regForm.lastName.trim(),
      email: regForm.email.trim(),
      phone: regForm.phone.trim(),
      password: regForm.password,
    });
    if ('user' in result) {
      toast.success(t.auth.registerButton + '!');
      onSuccess();
      return;
    }
    if (result.error === 'EMAIL_IN_USE') toast.error(t.auth.emailInUse);
    else if (result.error === 'RATE_LIMITED') toast.error('Too many attempts. Please wait a few minutes.');
    else toast.error(result.message || 'Registration failed');
  };

  const INPUT_CLS = "w-full px-4 py-3.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium focus:outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";
  const LABEL_CLS = "block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center pt-24 pb-28 md:pb-10 px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          <Lock size={32} className="mx-auto text-black/20 dark:text-white/20 mb-3" />
          <h1 className="text-2xl font-syncopate font-bold uppercase tracking-tight">
            {authTab === 'login' ? t.auth.loginToCheckout : t.auth.registerToCheckout}
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-black/[0.03] dark:bg-white/[0.03] rounded-xl p-1">
          {(['login', 'register'] as AuthTab[]).map((tb) => (
            <button
              key={tb}
              onClick={() => setAuthTab(tb)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-[10px] font-bold uppercase tracking-[0.15em] transition-all ${
                authTab === tb
                  ? 'bg-black dark:bg-white text-white dark:text-black shadow-sm'
                  : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60'
              }`}
            >
              {tb === 'login' ? <LogIn size={14} /> : <UserPlus size={14} />}
              {tb === 'login' ? t.auth.login : t.auth.register}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {authTab === 'login' ? (
            <motion.form
              key="login"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              onSubmit={handleLogin}
              className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 space-y-5"
            >
              <div>
                <label className={LABEL_CLS}>{t.auth.email}</label>
                <input type="email" value={loginForm.email} onChange={(e) => setLoginForm((p) => ({ ...p, email: e.target.value }))} className={INPUT_CLS} placeholder="email@example.com" required />
              </div>
              <div>
                <label className={LABEL_CLS}>{t.auth.password}</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={loginForm.password} onChange={(e) => setLoginForm((p) => ({ ...p, password: e.target.value }))} className={INPUT_CLS + ' pr-12'} placeholder="••••••" required />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" className="w-full py-4 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.3em] rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2">
                <LogIn size={14} /> {t.auth.loginButton}
              </button>
            </motion.form>
          ) : (
            <motion.form
              key="register"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              onSubmit={handleRegister}
              className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLS}>{t.auth.firstName}</label>
                  <input type="text" value={regForm.firstName} onChange={(e) => setRegForm((p) => ({ ...p, firstName: e.target.value }))} className={INPUT_CLS} required />
                </div>
                <div>
                  <label className={LABEL_CLS}>{t.auth.lastName}</label>
                  <input type="text" value={regForm.lastName} onChange={(e) => setRegForm((p) => ({ ...p, lastName: e.target.value }))} className={INPUT_CLS} required />
                </div>
              </div>
              <div>
                <label className={LABEL_CLS}>{t.auth.email}</label>
                <input type="email" value={regForm.email} onChange={(e) => setRegForm((p) => ({ ...p, email: e.target.value }))} className={INPUT_CLS} placeholder="email@example.com" required />
              </div>
              <div>
                <label className={LABEL_CLS}>{t.auth.phone}</label>
                <input type="tel" value={regForm.phone} onChange={(e) => setRegForm((p) => ({ ...p, phone: e.target.value }))} className={INPUT_CLS} placeholder="+995 XXX XXX XXX" />
              </div>
              <div>
                <label className={LABEL_CLS}>{t.auth.password}</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={regForm.password} onChange={(e) => setRegForm((p) => ({ ...p, password: e.target.value }))} className={INPUT_CLS + ' pr-12'} placeholder="••••••" required />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className={LABEL_CLS}>{t.auth.confirmPassword}</label>
                <input type={showPw ? 'text' : 'password'} value={regForm.confirmPassword} onChange={(e) => setRegForm((p) => ({ ...p, confirmPassword: e.target.value }))} className={INPUT_CLS} placeholder="••••••" required />
              </div>
              <button type="submit" className="w-full py-4 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.3em] rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2">
                <UserPlus size={14} /> {t.auth.registerButton}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

/* ========== MAIN CHECKOUT ========== */
const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, localize } = useTranslation();
  const language = t.nav.home === 'Home' ? 'en' : 'ka';
  const { formatPrice, currency: userCurrency } = useCurrency();
  const { items, subtotal, totalItems, clearCart } = useCartStore();
  const siteSettings = useAdminStore((s) => s.settings);
  const addOrder = useAdminStore((s) => s.addOrder);
  const currentUser = useUserStore((s) => s.currentUser);

  // Border colour is appended per field so an errored input can be told apart at a glance.
  const FIELD_BASE = "w-full px-4 py-3.5 bg-black/[0.03] dark:bg-white/[0.05] border rounded-xl text-sm font-medium focus:outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";

  const [step, setStep] = useState<Step>('shipping');
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [authPassed, setAuthPassed] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('pickup');

  // Admin can close either way of receiving an order. With only one left there is nothing to
  // choose, so the toggle is dropped and that one is used.
  const deliveryOptions = siteSettings?.deliveryMethodsEnabled ?? { pickup: true, delivery: true };
  const pickupAllowed = deliveryOptions.pickup !== false;
  const deliveryAllowed = deliveryOptions.delivery !== false;
  const bothAllowed = pickupAllowed && deliveryAllowed;
  const [sameAsShipping, setSameAsShipping] = useState(true);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const emptyAddress: Address = { address: '', address2: '', city: '', state: '', country: language === 'ka' ? 'საქართველო' : 'Georgia', postalCode: '' };
  const [shippingAddr, setShippingAddr] = useState<Address>(emptyAddress);
  const [billingAddr, setBillingAddr] = useState<Address>(emptyAddress);

  // Georgian Post integration
  const [gpostCountries, setGpostCountries] = useState<Array<{ CountryId: number; CountryNameEn: string; CountryNameGe: string }>>([]);
  const [gpostCities, setGpostCities] = useState<Array<{ CityId: number; CityNameEn: string; CityNameGe: string }>>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<number>(63); // 63 = Georgia (per gpost API)
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [shippingQuote, setShippingQuote] = useState<{ priceGEL: number; amount: number; currency: string; token: string; expiresAt: number; inputKey: string; deliveryEstimate: string } | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  // Settings land after the first paint, so the default choice has to be corrected once they
  // do — otherwise a buyer could sit on a method the gallery has closed.
  useEffect(() => {
    if (deliveryMethod === 'pickup' && !pickupAllowed && deliveryAllowed) { setDeliveryMethod('delivery'); setStep('shipping'); }
    if (deliveryMethod === 'delivery' && !deliveryAllowed && pickupAllowed) { setDeliveryMethod('pickup'); setStep('shipping'); }
  }, [deliveryMethod, pickupAllowed, deliveryAllowed]);

  // Pre-fill from user profile when user logs in or auth passes
  useEffect(() => {
    if (currentUser) {
      setForm({
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        email: currentUser.email,
        phone: currentUser.phone,
      });
      const savedShipping = currentUser.shippingAddress ?? currentUser.address ?? emptyAddress;
      setShippingAddr(savedShipping.country ? savedShipping : emptyAddress);
      if (currentUser.billingAddress) {
        setBillingAddr(currentUser.billingAddress);
        setSameAsShipping(false);
      }
    }
  }, [currentUser]);

  // Labels follow the chosen language; SearchableSelect sorts and filters them. Georgia
  // stays the default selection (selectedCountryId starts at 63) and is listed under G like
  // every other country, so the ordering has no special cases to explain.
  const countryOptions: SearchableOption[] = useMemo(() => {
    if (gpostCountries.length === 0) return [{ value: 63, label: language === 'ka' ? 'საქართველო' : 'Georgia' }];
    return gpostCountries.map((c) => ({
      value: c.CountryId,
      label: language === 'ka' ? c.CountryNameGe : c.CountryNameEn,
    }));
  }, [gpostCountries, language]);

  // Labelled "California (CA)" so either the name or the code finds it; the stored value is
  // the code, which is what carriers and customs forms expect.
  const usStateOptions: SearchableOption[] = useMemo(
    () => US_STATES.map((st, i) => ({ value: i, label: `${st.name} (${st.code})` })),
    [],
  );
  const shippingIsUs = isUnitedStates(shippingAddr.country || '');
  const usStateValue = useMemo(() => {
    const idx = US_STATES.findIndex((st) => st.code === shippingAddr.state);
    return idx >= 0 ? idx : null;
  }, [shippingAddr.state]);

  const cityOptions: SearchableOption[] = useMemo(
    () => gpostCities.map((c) => ({ value: c.CityId, label: language === 'ka' ? c.CityNameGe : c.CityNameEn })),
    [gpostCities, language],
  );

  // Load gpost countries on mount
  useEffect(() => {
    fetch('/api/v1/shipping/countries')
      .then((r) => r.json())
      .then((res) => {
        if (res.success && res.data?.Countries) setGpostCountries(res.data.Countries);
      })
      .catch(() => {});
  }, []);

  // Resolve stored bilingual labels back to carrier IDs before requesting a tariff.
  useEffect(() => {
    const country = gpostCountries.find(c => [c.CountryNameEn, c.CountryNameGe].some(n => n?.toLowerCase() === shippingAddr.country?.toLowerCase()));
    if (country) setSelectedCountryId(country.CountryId);
    else if (gpostCountries.length) setSelectedCountryId(0);
  }, [gpostCountries, shippingAddr.country]);

  useEffect(() => {
    const controller = new AbortController();
    setGpostCities([]);
    setSelectedCityId(null);
    if (!selectedCountryId) return () => controller.abort();
    fetch(`/api/v1/shipping/cities?countryId=${selectedCountryId}`, { signal: controller.signal })
      .then(r => r.json()).then(res => {
        if (!controller.signal.aborted && res.success && res.data?.Cities) setGpostCities(res.data.Cities);
      }).catch(() => {});
    return () => controller.abort();
  }, [selectedCountryId]);

  useEffect(() => {
    const city = gpostCities.find(c => [c.CityNameEn, c.CityNameGe].some(n => n?.toLowerCase() === shippingAddr.city?.toLowerCase()));
    setSelectedCityId(city?.CityId ?? null);
  }, [gpostCities, shippingAddr.city]);

  const quoteItems = items.map(item => ({ artworkId: item.artwork._id, quantity: item.quantity, frame: item.frame || 'unframed' }));
  const quoteInputKey = JSON.stringify({ items: quoteItems, address: shippingAddr, countryId: selectedCountryId, cityId: selectedCityId, userId: currentUser?._id, deliveryMethod });
  const [quoteAttempt, setQuoteAttempt] = useState(0);
  const validQuote = shippingQuote?.inputKey === quoteInputKey && shippingQuote.expiresAt > Date.now() ? shippingQuote : null;

  useEffect(() => {
    const controller = new AbortController();
    setShippingQuote(null);
    setQuoteError(null);
    setQuoteLoading(false);
    if (deliveryMethod !== 'delivery' || !selectedCityId || !selectedCountryId || !items.length ||
        !shippingAddr.address?.trim() || !shippingAddr.postalCode?.trim() || (shippingIsUs && !shippingAddr.state)) return;
    setQuoteLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const token = localStorage.getItem('gallery-auth-token');
        const response = await fetch('/api/v1/shipping/checkout-quote', {
          method: 'POST', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ items: quoteItems, address: shippingAddr, countryId: selectedCountryId, cityId: selectedCityId }),
        });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.error?.message || 'Could not calculate shipping price');
        if (!controller.signal.aborted) setShippingQuote({ ...result.data, inputKey: quoteInputKey, deliveryEstimate: '' });
      } catch (err) {
        if (!controller.signal.aborted) setQuoteError((err as Error).message);
      } finally { if (!controller.signal.aborted) setQuoteLoading(false); }
    }, 500);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [quoteInputKey, quoteAttempt]);

  useEffect(() => {
    if (!shippingQuote) return;
    const timer = window.setTimeout(() => setQuoteAttempt(n => n + 1), Math.max(0, shippingQuote.expiresAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [shippingQuote]);

  const sub = subtotal();
  // Cart currency derives from the first item — cart is single-currency since the site has one default.
  const cartCurrency = (items[0]?.artwork.price.currency || siteSettings.defaultCurrency || 'GEL') as 'USD' | 'GEL';
  const shippingCost = deliveryMethod === 'delivery' && validQuote ? validQuote.amount : 0;
  const tax = Math.round(sub * (siteSettings.taxPercentage / 100));
  const total = sub + shippingCost + tax;

  const steps: Array<{ id: Step; label: string; icon: React.ReactNode }> = [
    { id: 'shipping', label: t.checkout.shippingDetails, icon: <Truck size={14} /> },
    { id: 'payment', label: t.checkout.paymentMethod, icon: <CreditCard size={14} /> },
    { id: 'confirm', label: t.checkout.orderSummary, icon: <Package size={14} /> },
  ];

  const stepIndex = steps.findIndex((s) => s.id === step);

  const handleInput = (field: string, value: string): void => {
    setForm((prev) => ({ ...prev, [field]: value }));
    clearError(field);
  };

  const handleShippingAddr = (field: keyof Address, value: string): void => {
    setShippingAddr((prev) => ({ ...prev, [field]: value }));
    clearError(field);
  };
  const handleBillingAddr = (field: keyof Address, value: string): void => {
    setBillingAddr((prev) => ({ ...prev, [field]: value }));
  };

  // Nothing is marked red until the visitor presses Continue; after that a field clears as
  // soon as they start fixing it, so the form stops nagging about what is already handled.
  const [errors, setErrors] = useState<FieldErrors>({});
  const clearError = (field: string): void => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  /** Top-to-bottom, so an invalid form always sends the visitor to its first problem. */
  const FIELD_ORDER = [
    'firstName', 'lastName', 'email', 'phone',
    'address', 'country', 'city', 'state', 'postalCode',
  ] as const;

  function validateShipping(): FieldErrors {
    const found: FieldErrors = {};
    const req = t.form.requiredField;

    if (isBlank(form.firstName)) found.firstName = req;
    if (isBlank(form.lastName)) found.lastName = req;
    if (isBlank(form.email)) found.email = req;
    else if (!isEmail(form.email)) found.email = t.auth.invalidEmail;

    // Pickup needs no courier and no address, so only the delivery path asks for them.
    if (deliveryMethod === 'delivery') {
      if (isBlank(form.phone)) found.phone = t.checkout.phoneRequired;
      if (isBlank(shippingAddr.address)) found.address = req;
      if (isBlank(shippingAddr.country) || !selectedCountryId) found.country = req;
      if (isBlank(shippingAddr.city) || !selectedCityId) found.city = req;
      if (isBlank(shippingAddr.postalCode)) found.postalCode = req;
      // US carriers route on the state code, so it is not optional there.
      if (shippingIsUs && isBlank(shippingAddr.state)) found.state = req;
    }
    return found;
  }

  const handleContinueFromShipping = (): void => {
    const found = validateShipping();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error(t.checkout.fixErrors);
      focusFirstError(FIELD_ORDER, found);
      return;
    }
    if ((!pickupAllowed && deliveryMethod === 'pickup') || (!deliveryAllowed && deliveryMethod === 'delivery')) { toast.error('This delivery method is unavailable'); return; }
    if (deliveryMethod === 'delivery' && (!validQuote || validQuote.currency !== cartCurrency)) { toast.error(language === 'ka' ? 'დაელოდეთ მიტანის ფასს ან სცადეთ ხელახლა' : 'Wait for a shipping price or retry'); return; }
    setStep('payment');
  };

  const [placingOrder, setPlacingOrder] = useState(false);

  /**
   * Pulls the server's error code out of a thrown value.
   * `ApiError` carries it on `.code`, while the BOG branch throws a plain Error whose
   * message *is* the code — check both so one shape does not silently fall through.
   */
  const errorCodeOf = (err: unknown): string => {
    const e = err as { code?: string; message?: string };
    return `${e?.code ?? ''} ${e?.message ?? ''}`;
  };

  // ── Payment providers ──────────────────────────────────────────────
  // The backend reports which gateways are configured AND enabled in admin settings.
  interface PaymentProvider {
    id: 'bog' | 'unipay';
    name: { ka: string; en: string };
    methods: string[];
  }
  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<'bog' | 'unipay' | null>(null);
  const [providersLoading, setProvidersLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/v1/payments/providers');
        const data = await res.json();
        if (cancelled) return;
        const list = (data?.data?.providers ?? []) as PaymentProvider[];
        setProviders(list);
        // Preselect the first available gateway so the customer never faces an empty step.
        if (list.length > 0 && list[0]) setSelectedProvider(list[0].id);
      } catch {
        if (!cancelled) setProviders([]);
      } finally {
        if (!cancelled) setProvidersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handlePlaceOrder = async (): Promise<void> => {
    if (placingOrder) return;
    if (!selectedProvider || !providers.some(p => p.id === selectedProvider)) { toast.error('Payments are unavailable. Please try again later.'); return; }
    const found = validateShipping();
    if (Object.keys(found).length || (deliveryMethod === 'delivery' && (!validQuote || validQuote.currency !== cartCurrency)) || (deliveryMethod === 'pickup' ? !pickupAllowed : !deliveryAllowed)) {
      setErrors(found); setStep('shipping'); toast.error(t.checkout.fixErrors); return;
    }
    setPlacingOrder(true);

    try {
      const finalBilling = sameAsShipping ? shippingAddr : billingAddr;

      // Step 1: Create order in backend
      const orderData = {
        userId: currentUser?._id,
        items: items.map((item) => ({
          artworkId: item.artwork._id,
          artworkTitle: item.artwork.title,
          artworkImage: item.artwork.images?.[0] || '',
          quantity: item.quantity,
          frame: item.frame,
          unitPrice: item.artwork.price.amount,
          totalPrice: item.totalPrice,
        })),
        customer: {
          ...form,
          address: shippingAddr.address,
          city: shippingAddr.city,
          country: shippingAddr.country,
          postalCode: shippingAddr.postalCode,
        },
        deliveryMethod,
        shippingAddress: shippingAddr,
        billingAddress: finalBilling,
        subtotal: sub,
        shipping: shippingCost,
        shippingQuoteToken: deliveryMethod === 'delivery' ? validQuote?.token : undefined,
        taxRate: siteSettings.taxPercentage / 100,
        tax,
        total,
        currency: cartCurrency,
        status: 'pending',
      };

      // Step 1: Create order via API (wait for real MongoDB ID)
      const { publicWriteApi, paymentApi } = await import('@/lib/api');
      let realOrderId: string;
      try {
        const apiOrder = await publicWriteApi.createOrder(orderData) as { _id: string };
        realOrderId = apiOrder._id;
        console.log('[Checkout] Order created:', realOrderId);
      } catch (orderErr) {
        console.error('[Checkout] Order creation failed:', orderErr);
        const msg = errorCodeOf(orderErr);
        if (msg.includes('ORDER_NOT_ALLOWED')) {
          // Shop is still in testing mode and this address is not on the allow list.
          toast.error(language === 'ka'
            ? 'ონლაინ შეკვეთა სატესტო რეჟიმშია — გთხოვთ დაგვიკავშირდეთ info@galleryfotoatelier.com'
            : 'Online ordering is in testing mode — please contact info@galleryfotoatelier.com');
        } else if (msg.includes('SHIPPING_QUOTE_REQUIRED') || msg.includes('DELIVERY_METHOD_DISABLED')) {
          setStep('shipping'); setShippingQuote(null); setQuoteAttempt(n => n + 1);
          void useAdminStore.getState().refreshSettings();
          toast.error(language === 'ka' ? 'განაახლეთ მიტანის მონაცემები' : 'Please refresh the delivery details');
        } else {
          toast.error(language === 'ka' ? 'შეკვეთის შექმნა ვერ მოხერხდა' : 'Failed to create order');
        }
        setPlacingOrder(false);
        return;
      }

      // Step 2: Open a checkout session with the provider the customer picked
      try {
        const idempotencyKey = `${realOrderId}-${Date.now()}`;
        let checkoutUrl = '';

        if (selectedProvider === 'bog') {
          const token = localStorage.getItem('gallery-auth-token') || '';
          const res = await fetch('/api/v1/payments/bog/create-checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              orderId: realOrderId,
              language: language === 'ka' ? 'ka' : 'en',
              idempotencyKey,
            }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data?.error?.code || 'PAYMENT_ERROR');
          checkoutUrl = data?.data?.checkoutUrl || '';
        } else {
          const checkout = await paymentApi.createCheckout({
            orderId: realOrderId,
            currency: 'GEL',
            language: language === 'ka' ? 'GE' : 'EN',
            idempotencyKey,
          });
          checkoutUrl = checkout.checkoutUrl || '';
        }

        if (!checkoutUrl) throw new Error('PAYMENT_ERROR');
        if (checkoutUrl) {
          // Step 3: Redirect to the provider's hosted payment page
          window.location.href = checkoutUrl;
          return;
        }
      } catch (payErr: unknown) {
        console.error('[Checkout] Payment error:', payErr);
        const msg = errorCodeOf(payErr);
        if (msg.includes('ALREADY_PAID')) {
          // Customer came back to a settled order — send them to their orders
          // rather than inviting a second payment attempt.
          toast.success(language === 'ka' ? 'ეს შეკვეთა უკვე გადახდილია' : 'This order has already been paid');
          clearCart();
          navigate('/profile');
          return;
        }
        if (msg.includes('UNAUTHORIZED')) {
          toast.error(language === 'ka' ? 'სესია ამოიწურა — გთხოვთ თავიდან შეხვიდეთ' : 'Your session expired — please sign in again');
        } else if (msg.includes('PAYMENTS_DISABLED')) {
          toast.error(language === 'ka' ? 'ონლაინ გადახდა დროებით გათიშულია' : 'Online payments are temporarily disabled');
        } else if (msg.includes('BOG_NOT_CONFIGURED')) {
          toast.error(language === 'ka' ? 'ეს გადახდის სისტემა ჯერ არ არის აქტიური' : 'This payment provider is not active yet');
        } else if (msg.includes('PAYMENT_NOT_ALLOWED')) {
          toast.error(language === 'ka' ? 'ონლაინ გადახდა სატესტო რეჟიმშია' : 'Online payments are in testing mode');
        } else {
          toast.error(language === 'ka' ? 'გადახდის შეცდომა — სცადეთ თავიდან' : 'Payment error — please try again');
        }
        setPlacingOrder(false);
        return;
      }

      // Success is shown only by the verified payment-result flow.
    } catch (err) {
      console.error('[Checkout] Order error:', err);
      toast.error(language === 'ka' ? 'შეკვეთის შეცდომა' : 'Order creation failed');
    }
    setPlacingOrder(false);
  };

  // Empty cart
  if (items.length === 0 && !orderPlaced) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-24 px-4">
        <Package size={48} className="text-black/10 dark:text-white/10 mb-4" />
        <p className="text-black/30 dark:text-white/30 text-sm font-bold uppercase tracking-widest mb-6">
          {t.cart.empty}
        </p>
        <Link
          to="/shop"
          className="px-8 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.3em] rounded-2xl hover:opacity-90 transition-all"
        >
          {t.artwork.backToShop}
        </Link>
      </div>
    );
  }

  // Auth gate — require login/register before checkout
  if (!currentUser && !authPassed) {
    return <AuthGate onSuccess={() => setAuthPassed(true)} />;
  }

  // Order placed success
  if (orderPlaced) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-24 px-4">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mb-6"
        >
          <Check size={36} className="text-white" />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-3xl font-syncopate font-bold uppercase tracking-tight mb-3"
        >
          Thank You
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-black/40 dark:text-white/40 text-sm mb-8 text-center max-w-md"
        >
          Your order has been placed successfully. You will receive a confirmation email shortly.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="flex gap-3"
        >
          <Link
            to="/profile"
            className="px-8 py-4 border border-black/10 dark:border-white/10 text-[10px] font-bold uppercase tracking-[0.2em] rounded-2xl hover:bg-black/5 dark:hover:bg-white/5 transition-all"
          >
            My Orders
          </Link>
          <Link
            to="/shop"
            className="px-10 py-4 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.3em] rounded-2xl hover:opacity-90 transition-all"
          >
            Continue Shopping
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-black transition-colors duration-700">
      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors mb-6"
        >
          <ArrowLeft size={14} />
          {t.checkout.backToCart}
        </button>

        <h1 className="text-3xl sm:text-4xl font-syncopate font-bold uppercase tracking-tight mb-8">
          {t.checkout.title}
        </h1>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-10">
          {steps.map((s, i) => (
            <React.Fragment key={s.id}>
              <button
                onClick={() => i <= stepIndex && setStep(s.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-[9px] font-bold uppercase tracking-widest transition-all ${
                  step === s.id
                    ? 'bg-black dark:bg-white text-white dark:text-black'
                    : i < stepIndex
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                    : 'bg-black/5 dark:bg-white/5 text-black/30 dark:text-white/30'
                }`}
              >
                {i < stepIndex ? <Check size={12} /> : s.icon}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-px max-w-12 ${i < stepIndex ? 'bg-green-500/30' : 'bg-black/5 dark:bg-white/5'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">

          {/* Form Side */}
          <div className="lg:col-span-3">
            {step === 'shipping' && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                {/* Delivery Method Toggle */}
                <div className="space-y-3">
                  <h2 className="text-sm font-bold uppercase tracking-widest text-black/50 dark:text-white/50 flex items-center gap-2">
                    <Truck size={16} /> {t.checkout.deliveryMethod}
                  </h2>
                  {bothAllowed ? (
                    <div className="flex gap-2 bg-black/[0.02] dark:bg-white/[0.02] rounded-xl p-1">
                      <button
                        onClick={() => { setDeliveryMethod('pickup'); setErrors({}); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-[10px] font-bold uppercase tracking-[0.15em] transition-all ${
                          deliveryMethod === 'pickup'
                            ? 'bg-black dark:bg-white text-white dark:text-black shadow-sm'
                            : 'text-black/30 dark:text-white/30'
                        }`}
                      >
                        <Store size={14} /> {t.checkout.pickup}
                      </button>
                      <button
                        onClick={() => { setDeliveryMethod('delivery'); setErrors({}); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-[10px] font-bold uppercase tracking-[0.15em] transition-all ${
                          deliveryMethod === 'delivery'
                            ? 'bg-black dark:bg-white text-white dark:text-black shadow-sm'
                            : 'text-black/30 dark:text-white/30'
                        }`}
                      >
                        <Truck size={14} /> {t.checkout.delivery}
                      </button>
                    </div>
                  ) : (
                    // Only one way left — state it plainly instead of showing a choice of one.
                    <div className="flex items-center gap-2 py-3 px-4 bg-black/[0.02] dark:bg-white/[0.02] rounded-xl text-[10px] font-bold uppercase tracking-[0.15em]">
                      {deliveryMethod === 'pickup' ? <Store size={14} /> : <Truck size={14} />}
                      {deliveryMethod === 'pickup' ? t.checkout.pickup : t.checkout.delivery}
                    </div>
                  )}
                </div>

                {/* Personal Info */}
                <div className="space-y-4">
                  <h3 className="text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">
                    {t.checkout.shippingDetails}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <FormLabel className="block text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 mb-2" required>{t.checkout.firstName}</FormLabel>
                      <input data-field="firstName" type="text" value={form.firstName} onChange={(e) => handleInput('firstName', e.target.value)} className={inputBorder(FIELD_BASE, !!errors.firstName)} placeholder={t.checkout.firstName} />
                      <FieldError message={errors.firstName} />
                    </div>
                    <div>
                      <FormLabel className="block text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 mb-2" required>{t.checkout.lastName}</FormLabel>
                      <input data-field="lastName" type="text" value={form.lastName} onChange={(e) => handleInput('lastName', e.target.value)} className={inputBorder(FIELD_BASE, !!errors.lastName)} placeholder={t.checkout.lastName} />
                      <FieldError message={errors.lastName} />
                    </div>
                  </div>
                  <div>
                    <FormLabel className="block text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 mb-2" required>{t.checkout.email}</FormLabel>
                    <input data-field="email" type="email" value={form.email} onChange={(e) => handleInput('email', e.target.value)} className={inputBorder(FIELD_BASE, !!errors.email)} placeholder="email@example.com" />
                    <FieldError message={errors.email} />
                  </div>
                  <div>
                    <FormLabel className="block text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 mb-2" required={deliveryMethod === 'delivery'} optional={deliveryMethod !== 'delivery'}>
                      {t.checkout.phone}
                    </FormLabel>
                    <input data-field="phone" type="tel" value={form.phone} onChange={(e) => handleInput('phone', e.target.value)} className={inputBorder(FIELD_BASE, !!errors.phone)} placeholder="+995 XXX XXX XXX" />
                    <FieldError message={errors.phone} />
                  </div>
                </div>

                {/* Shipping & Billing Address — only for delivery */}
                {deliveryMethod === 'delivery' && (
                  <>
                    <div className="space-y-4">
                      <h3 className="text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">
                        {t.checkout.shippingAddress}
                      </h3>
                      <div>
                        <FormLabel className="block text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 mb-2" required>{t.checkout.address}</FormLabel>
                        <input data-field="address" type="text" value={shippingAddr.address} onChange={(e) => handleShippingAddr('address', e.target.value)} className={inputBorder(FIELD_BASE, !!errors.address)} placeholder={t.checkout.address} />
                        <FieldError message={errors.address} />
                      </div>
                      <div>
                        <FormLabel className="block text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 mb-2" optional>{t.checkout.address2}</FormLabel>
                        <input type="text" value={shippingAddr.address2 ?? ''} onChange={(e) => handleShippingAddr('address2', e.target.value)} className={inputBorder(FIELD_BASE, false)} placeholder={t.checkout.address2} />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <FormLabel className="block text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 mb-2" required>{t.checkout.country}</FormLabel>
                          <div data-field="country" className={errors.country ? 'rounded-xl ring-1 ring-red-400' : undefined}>
                          <SearchableSelect
                            options={countryOptions}
                            value={selectedCountryId}
                            onChange={(id, option) => {
                              setSelectedCountryId(id);
                              setSelectedCityId(null); setGpostCities([]);
                              setShippingAddr(a => ({ ...a, country: option.label, city: '', state: '' }));
                              clearError('country');
                            }}
                            placeholder={language === 'ka' ? 'აირჩიე ქვეყანა' : 'Select country'}
                            emptyText={language === 'ka' ? 'ვერ მოიძებნა' : 'No matches'}
                          />
                          </div>
                          <FieldError message={errors.country} />
                        </div>
                        <div>
                          <FormLabel className="block text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 mb-2" required>{t.checkout.city}</FormLabel>
                          <div data-field="city" className={errors.city ? 'rounded-xl ring-1 ring-red-400' : undefined}>
                          <SearchableSelect
                            options={cityOptions}
                            value={selectedCityId}
                            onChange={(id, option) => {
                              setSelectedCityId(id);
                              handleShippingAddr('city', option.label);
                            }}
                            disabled={cityOptions.length === 0}
                            placeholder={
                              cityOptions.length === 0
                                ? language === 'ka' ? 'იტვირთება…' : 'Loading…'
                                : language === 'ka' ? 'აირჩიე ქალაქი' : 'Select city'
                            }
                            emptyText={language === 'ka' ? 'ვერ მოიძებნა' : 'No matches'}
                          />
                          </div>
                          <FieldError message={errors.city} />
                        </div>
                      </div>
                      <div>
                        <FormLabel className="block text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 mb-2" required={shippingIsUs} optional={!shippingIsUs}>
                          {shippingIsUs ? t.checkout.stateUs : t.checkout.state}
                        </FormLabel>
                        {shippingIsUs ? (
                          // Carriers want the two-letter code, so the US picker stores it rather
                          // than whatever spelling the buyer would have typed.
                          <div data-field="state" className={errors.state ? 'rounded-xl ring-1 ring-red-400' : undefined}>
                            <SearchableSelect
                              options={usStateOptions}
                              value={usStateValue}
                              onChange={(id) => handleShippingAddr('state', US_STATES[id]?.code ?? '')}
                              placeholder={language === 'ka' ? 'აირჩიე შტატი' : 'Select state'}
                              emptyText={language === 'ka' ? 'ვერ მოიძებნა' : 'No matches'}
                            />
                          </div>
                        ) : (
                          <input data-field="state" type="text" value={shippingAddr.state ?? ''} onChange={(e) => handleShippingAddr('state', e.target.value)} className={inputBorder(FIELD_BASE, !!errors.state)} placeholder={t.checkout.state} />
                        )}
                        <FieldError message={errors.state} />
                      </div>
                      <div>
                        <FormLabel className="block text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 mb-2" required>{t.checkout.postalCode}</FormLabel>
                        <input data-field="postalCode" type="text" value={shippingAddr.postalCode} onChange={(e) => handleShippingAddr('postalCode', e.target.value)} className={inputBorder(FIELD_BASE, !!errors.postalCode)} placeholder={t.checkout.postalCode} />
                        <FieldError message={errors.postalCode} />
                      </div>
                      {/* Live shipping quote */}
                      {selectedCityId && (
                        <div className="p-4 rounded-xl bg-black/[0.02] dark:bg-white/[0.03] border border-black/[0.05] dark:border-white/[0.05] flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Truck size={16} className="text-black/40 dark:text-white/40" />
                            <div>
                              <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">
                                {language === 'ka' ? 'მიწოდების ფასი (ფოსტა)' : 'Shipping (Georgian Post)'}
                              </p>
                              {quoteLoading ? (
                                <p className="text-[11px] text-black/50 dark:text-white/50 mt-0.5">Calculating...</p>
                              ) : validQuote ? (
                                <p className="text-[12px] font-bold mt-0.5">
                                  {shippingQuote.priceGEL.toFixed(2)} ₾ · {validQuote.deliveryEstimate}
                                </p>
                              ) : quoteError ? (
                                <div><p className="text-[10px] text-red-500 mt-0.5">{quoteError}</p><button type="button" onClick={() => setQuoteAttempt(n => n + 1)} className="underline text-xs">{language === 'ka' ? 'სცადეთ ხელახლა' : 'Try again'}</button></div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Billing Address removed — UniPay handles card verification */}
                  </>
                )}

                <RequiredNote />

                <button
                  onClick={handleContinueFromShipping}
                  className="w-full sm:w-auto px-12 py-4 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.3em] rounded-2xl hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-3"
                >
                  Continue <ChevronRight size={14} />
                </button>
              </motion.div>
            )}

            {step === 'payment' && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h2 className="text-sm font-bold uppercase tracking-widest text-black/50 dark:text-white/50 flex items-center gap-2">
                  <CreditCard size={16} /> {t.checkout.paymentMethod}
                </h2>

                <div className="space-y-4">
                  {providersLoading ? (
                    <div className="p-6 rounded-2xl border border-black/5 dark:border-white/5 bg-[#fafafa] dark:bg-[#0a0a0a] text-center">
                      <span className="inline-block w-5 h-5 border-2 border-black/10 dark:border-white/10 border-t-black dark:border-t-white rounded-full animate-spin" />
                    </div>
                  ) : providers.length === 0 ? (
                    <div className="p-6 rounded-2xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/5 text-center">
                      <p className="text-sm font-bold text-amber-800 dark:text-amber-400">
                        {language === 'ka' ? 'ონლაინ გადახდა დროებით მიუწვდომელია' : 'Online payment is temporarily unavailable'}
                      </p>
                      <p className="text-[11px] text-amber-700/70 dark:text-amber-400/60 mt-1">
                        {language === 'ka' ? 'გთხოვთ დაგვიკავშირდეთ: info@galleryfotoatelier.com' : 'Please contact us: info@galleryfotoatelier.com'}
                      </p>
                    </div>
                  ) : (
                    <>
                      {providers.length > 1 && (
                        <p className="text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">
                          {language === 'ka' ? 'აირჩიეთ გადახდის სისტემა' : 'Choose payment provider'}
                        </p>
                      )}
                      {providers.map((p) => {
                        const isSelected = selectedProvider === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setSelectedProvider(p.id)}
                            aria-pressed={isSelected}
                            className={`w-full p-5 rounded-2xl border text-left transition-all ${
                              isSelected
                                ? 'border-black dark:border-white bg-white dark:bg-[#0a0a0a] shadow-sm'
                                : 'border-black/5 dark:border-white/5 bg-[#fafafa] dark:bg-[#0a0a0a] hover:border-black/20 dark:hover:border-white/20'
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isSelected ? 'bg-black dark:bg-white' : 'bg-black/5 dark:bg-white/5'}`}>
                                <CreditCard size={20} className={isSelected ? 'text-white dark:text-black' : 'text-black/40 dark:text-white/40'} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-black dark:text-white">
                                  {p.name[language === 'ka' ? 'ka' : 'en']}
                                </p>
                                <p className="text-[11px] text-black/40 dark:text-white/40 mt-0.5">
                                  {language === 'ka'
                                    ? 'ბარათი · Apple Pay · Google Pay'
                                    : 'Card · Apple Pay · Google Pay'}
                                </p>
                              </div>
                              {providers.length > 1 && (
                                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${isSelected ? 'border-black dark:border-white' : 'border-black/15 dark:border-white/15'}`}>
                                  {isSelected && <span className="w-2.5 h-2.5 rounded-full bg-black dark:bg-white" />}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                      <p className="text-[10px] text-black/35 dark:text-white/35 text-center pt-1">
                        {language === 'ka'
                          ? 'გადამისამართდებით არჩეული სისტემის უსაფრთხო გვერდზე. ბარათის მონაცემები ჩვენს სერვერზე არ ინახება.'
                          : 'You will be redirected to the provider’s secure page. Card details are never stored on our servers.'}
                      </p>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-500/5 border border-green-200 dark:border-green-500/10 rounded-xl">
                  <Lock size={14} className="text-green-600 dark:text-green-400 shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-green-700 dark:text-green-400">
                      {t.checkout.securePayment}
                    </p>
                    <p className="text-[9px] text-green-600/70 dark:text-green-400/60 mt-0.5">
                      {t.checkout.orderProtection}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep('shipping')}
                    className="px-8 py-4 border border-black/10 dark:border-white/10 text-[10px] font-bold uppercase tracking-[0.2em] rounded-2xl hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                  >
                    <ArrowLeft size={14} />
                  </button>
                  <button
                    onClick={() => setStep('confirm')}
                    disabled={!selectedProvider}
                    className="flex-1 py-4 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.3em] rounded-2xl hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    Review Order <ChevronRight size={14} />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 'confirm' && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h2 className="text-sm font-bold uppercase tracking-widest text-black/50 dark:text-white/50 flex items-center gap-2">
                  <Package size={16} /> {t.checkout.orderSummary}
                </h2>

                {/* Item List */}
                <div className="space-y-3">
                  {items.map((item) => (
                    <div
                      key={`${item.artwork._id}-${item.frame}`}
                      className="flex gap-4 p-4 border border-black/5 dark:border-white/5 rounded-2xl"
                    >
                      <img
                        src={item.artwork.images?.[0] || ''}
                        alt={localize(item.artwork.title)}
                        className="w-20 h-20 object-cover rounded-xl"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-bold uppercase tracking-tight truncate">
                          {localize(item.artwork.title)}
                        </h4>
                        <p className="text-[9px] text-black/40 dark:text-white/40 mt-1">
                          {item.frame} &middot; Qty: {item.quantity}
                        </p>
                      </div>
                      <span className="text-sm font-bold font-syncopate shrink-0 self-center">
                        {formatPrice(item.totalPrice, item.artwork.price.currency)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Customer & Delivery Info Summary */}
                {form.firstName && (
                  <div className="space-y-3">
                    <div className="p-4 bg-black/[0.02] dark:bg-white/[0.03] border border-black/5 dark:border-white/5 rounded-2xl space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-[9px] font-bold uppercase tracking-widest text-black/30 dark:text-white/30">
                          {deliveryMethod === 'delivery' ? t.checkout.shippingAddress : t.checkout.pickup}
                        </p>
                        <span className="text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/5 text-black/30 dark:text-white/30">
                          {deliveryMethod === 'delivery' ? t.checkout.delivery : t.checkout.pickup}
                        </span>
                      </div>
                      <p className="text-sm font-medium">{form.firstName} {form.lastName}</p>
                      {deliveryMethod === 'delivery' && (
                        <>
                          <p className="text-xs text-black/50 dark:text-white/50">{shippingAddr.address}</p>
                          <p className="text-xs text-black/50 dark:text-white/50">{shippingAddr.city}, {shippingAddr.country} {shippingAddr.postalCode}</p>
                        </>
                      )}
                      {form.phone && <p className="text-xs text-black/50 dark:text-white/50">{form.phone}</p>}
                    </div>
                    {/* Billing Address removed — UniPay handles card verification */}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep('payment')}
                    className="px-8 py-4 border border-black/10 dark:border-white/10 text-[10px] font-bold uppercase tracking-[0.2em] rounded-2xl hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                  >
                    <ArrowLeft size={14} />
                  </button>
                  <button
                    onClick={handlePlaceOrder}
                    disabled={placingOrder}
                    className="flex-1 py-5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.3em] rounded-2xl hover:opacity-90 active:scale-[0.98] transition-all shadow-xl flex items-center justify-center gap-3 disabled:opacity-50"
                  >
                    {placingOrder ? (
                      <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> {language === 'ka' ? 'მიმდინარეობს...' : 'Processing...'}</>
                    ) : (
                      <><ShieldCheck size={16} /> {t.checkout.placeOrder}</>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-2">
            <div className="sticky top-28 space-y-6">
              <div className="p-6 bg-[#fafafa] dark:bg-[#0a0a0a] border border-black/5 dark:border-white/5 rounded-2xl space-y-5 transition-colors duration-700">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">
                  {t.checkout.orderSummary} &middot; {totalItems()} {t.checkout.items}
                </h3>

                {/* Mini item previews */}
                <div className="flex -space-x-2">
                  {items.slice(0, 4).map((item) => (
                    <img
                      key={item.artwork._id}
                      src={item.artwork.images?.[0] || ''}
                      alt=""
                      className="w-12 h-12 rounded-lg object-cover border-2 border-[#fafafa] dark:border-[#0a0a0a]"
                    />
                  ))}
                  {items.length > 4 && (
                    <div className="w-12 h-12 rounded-lg bg-black/10 dark:bg-white/10 border-2 border-[#fafafa] dark:border-[#0a0a0a] flex items-center justify-center text-[10px] font-bold">
                      +{items.length - 4}
                    </div>
                  )}
                </div>

                <div className="space-y-3 pt-3 border-t border-black/5 dark:border-white/5">
                  <div className="flex justify-between text-xs">
                    <span className="text-black/40 dark:text-white/40">{t.cart.subtotal}</span>
                    <span className="font-semibold">{formatPrice(sub, cartCurrency)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-black/40 dark:text-white/40">{t.checkout.shipping}</span>
                    <span className="font-semibold">
                      {quoteLoading ? (
                        <span className="text-black/40 dark:text-white/40">Calculating...</span>
                      ) : validQuote ? (
                        formatPrice(shippingCost, cartCurrency)
                      ) : selectedCityId ? (
                        <span className="text-red-500 text-[10px]">Quote unavailable</span>
                      ) : (
                        <span className="text-black/30 dark:text-white/30 text-[10px]">Select city</span>
                      )}
                    </span>
                  </div>
                  {validQuote?.deliveryEstimate && (
                    <div className="flex justify-between text-[10px] text-black/40 dark:text-white/40">
                      <span>Delivery</span>
                      <span>{validQuote.deliveryEstimate}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs">
                    <span className="text-black/40 dark:text-white/40">{t.checkout.tax} ({siteSettings.taxPercentage}%)</span>
                    <span className="font-semibold">{formatPrice(tax, cartCurrency)}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-black/10 dark:border-white/10">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-black/50 dark:text-white/50">
                    {t.checkout.total}
                  </span>
                  <span className="text-2xl font-syncopate font-bold">
                    {formatPrice(total, cartCurrency)}
                  </span>
                </div>
              </div>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-6 px-4">
                {[
                  { icon: <ShieldCheck size={16} />, text: 'SSL Secure' },
                  { icon: <Truck size={16} />, text: 'Insured' },
                  { icon: <Lock size={16} />, text: 'Encrypted' },
                ].map((badge) => (
                  <div key={badge.text} className="flex items-center gap-1.5 text-[8px] font-bold uppercase tracking-widest text-black/25 dark:text-white/25">
                    {badge.icon}
                    {badge.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
